import { useCallback, useRef, useState, type ReactElement } from 'react';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon, ImageOffIcon, Loader2Icon, PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { adUsageLabelKey } from '../lib/ad-usage';
import type { MediaTile } from '../model/media-tile';

/**
 * Photographs to choose from, at whatever density the surface has room for.
 *
 * One grid for the panel and the library, so a thumbnail behaves the same in both: the bound one is
 * marked wherever it appears, and choosing is a click on the picture rather than on a control beside
 * it.
 *
 * In the library a tile answers two questions with two controls. Clicking the picture opens it;
 * ticking its checkbox puts it in the selection a bulk action will hit. Both at once is a normal
 * state -- you can read one asset while three are ticked -- so the frame is filled differently for
 * each rather than the two sharing a border.
 */
const COLUMN_CLASS = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  /**
   * Three up, four once there is room for a fourth at the same tile size.
   *
   * For a surface whose width a person sets: widening it should show more pictures rather than
   * bigger ones, which is what a fixed count does with the extra space. The step is read off the
   * grid's own container rather than the viewport, because the width that changed is a panel's and
   * the window never moved -- so **the host has to carry `@container`** for this to do anything.
   *
   * 440px is where a fourth 108px tile fits: below it four columns would draw them smaller than
   * the narrow case does, which is the opposite of the point.
   */
  // eslint-disable-next-line i18next/no-literal-string -- Tailwind classes, not copy
  panel: 'grid-cols-3 @min-[440px]:grid-cols-4',
  // A count the window decides rather than the caller: on a full-width surface a fixed count
  // stretches four tiles across a wide monitor instead of showing more of the library.
  fill: 'grid-cols-[repeat(auto-fill,minmax(180px,1fr))]',
} as const;

/**
 * The picture's own box, inside the frame the tile draws around it.
 *
 * It is the positioning context for everything laid over the shot, so a badge is placed against the
 * edge of the image rather than the edge of the frame and the two cannot drift apart. Its corners
 * are the tile's less the padding, which is what keeps the two curves concentric.
 */
const PICTURE_CLASS = 'relative size-full overflow-hidden rounded-sm bg-transparency-checker';

/**
 * The frame's fill and edge, per state, following the rows in `MediaTable` so a tile and a row say
 * the same thing the same way whichever view someone is in.
 *
 * `primary` and `accent` rather than a black and a beige: both invert with the theme, where two
 * fixed colours would leave the open tile a black hole in a dark library.
 */
const FRAME_CLASS = {
  default: 'border-border bg-card hover:border-border-strong',
  /** The one the detail panel is describing, which the table fills with its `active`. */
  open: 'border-primary bg-primary',
  /** Ticked for a bulk action, on the table's `selected`. */
  selected: 'border-border bg-accent',
  /** What a binding surface marks: the asset this frame uses. The library's fill rather than an
      edge of its own -- a hairline is a one-pixel shift from the border every other tile already
      carries, which is invisible in a grid of photographs, and the picked asset is the one thing
      someone comes to this panel to find. */
  bound: 'border-primary bg-primary',
} as const;

export const MediaGrid = ({
  tiles,
  columns,
  boundAssetId,
  selectedIds,
  openMediaId,
  onChoose,
  onClear,
  onToggleSelect,
  showAdUsage = false,
  revealsBound = true,
  namesOnHover = false,
}: {
  tiles: readonly MediaTile[];
  /**
   * A fixed count for a panel of known width; `panel` for one a person resizes, which needs the
   * host to be a `@container`; `fill` for a surface that owns the whole window.
   */
  columns: 2 | 3 | 4 | 'panel' | 'fill';
  /** Marked rather than filtered out: seeing what is bound among the alternatives is the point. */
  boundAssetId?: string;
  /**
   * Rows ticked for a bulk action, by media id rather than asset id.
   *
   * Separate from `boundAssetId` because they answer different questions: one asks what this frame
   * is using, the other what a person is about to act on. A surface uses one or the other. Its
   * presence, not its contents, is what switches the grid into the library's picking mode: an empty
   * set still means "picking, nothing ticked yet", not "not picking".
   */
  selectedIds?: ReadonlySet<string>;
  /**
   * The tile the detail panel is describing, by media id. Only ever one, and independent of what is
   * ticked: opening a tile does not select it, and ticking one does not open it.
   */
  openMediaId?: string;
  /**
   * Omit it for a grid that is there to be looked at rather than picked from. Tiles then render as
   * plain figures: a button that did nothing on click would read as broken rather than as reference.
   */
  onChoose?: (assetId: string) => void;
  /**
   * Unbinding, by clicking the bound tile again. Given one, the grid becomes a toggle rather than a
   * one-way pick: the marked tile empties the slot instead of re-choosing what it already holds,
   * which is what its `aria-pressed` has always claimed it would do.
   *
   * Omit it on a surface where a picture is picked rather than set -- the library, where a tile's
   * click opens it, and any grid whose slot must hold something.
   */
  onClear?: () => void;
  /**
   * Ticking a tile's checkbox, by media id -- the id the selection is keyed by, so the host has
   * nothing to look up. Omit it and no checkbox is drawn, which is what a binding surface wants: the
   * panel picks one asset for one frame and has no bulk action to select for.
   */
  onToggleSelect?: (mediaId: string) => void;
  /**
   * Draws each tile's `adUsage` tag over the picture. Off by default: it is a reading of how the
   * shot could function in a layout, useful while browsing a folder and noise on a surface where
   * someone is picking one asset for a frame they already have in mind.
   */
  showAdUsage?: boolean;
  /**
   * Brings the bound tile into view the first time it is drawn.
   *
   * On by default, because the surfaces this grid was written for are fixed-height galleries
   * where the marked tile can sit below the fold. Off for a grid embedded in a scroller
   * somebody else owns: a ref callback runs before an ancestor's layout effect, so a grid that
   * scrolls itself on mount lands underneath whatever that ancestor was about to restore.
   */
  revealsBound?: boolean;
  /**
   * Names each tile in a tooltip rather than leaving it to the browser's own.
   *
   * Off by default, which keeps the native `title` these galleries have always had. On where
   * the name is the only thing telling two pictures apart: the native one waits about a second,
   * styles itself to the OS and never appears for somebody on a keyboard.
   */
  namesOnHover?: boolean;
}) => {
  const { t } = useTranslation('tools');

  /**
   * One id rather than a flag per tile: only one tile is under the pointer, and the video element is
   * mounted only for that one. A grid of footage that mounted them all would have every clip
   * fetching bytes before anyone asked to see one move.
   */
  const [hovered, setHovered] = useState<string | null>(null);
  /** The hovered clip once its first frame is up, which is when it may cover the poster. */
  const [playingId, setPlayingId] = useState<string | null>(null);

  /**
   * Tiles whose `<img>` fired `onError` -- a signed URL that resolved but 403'd or 404'd on
   * fetch. Separate from `tile.url` being undefined: that case never mounts an `<img>` at all,
   * so it can never error.
   */
  const [imageFailed, setImageFailed] = useState<ReadonlySet<string>>(new Set());

  const picking = selectedIds !== undefined;

  /**
   * Brings the bound tile into view the first time the grid draws it.
   *
   * A gallery in a fixed-height box holds more tiles than it shows, so the frame's own picture can
   * sit below the fold where no marker on it can be found at all. A ref callback rather than an
   * effect because the tiles arrive asynchronously: this fires when the node mounts, whenever that
   * turns out to be.
   *
   * `nearest` scrolls only the scroller that has to move, and not at all when the tile is already
   * on screen -- so choosing a picture never yanks the grid under the pointer that chose it. Once
   * per asset, so a refetch cannot pull someone back from wherever they have since scrolled.
   */
  const revealed = useRef<string | undefined>(undefined);
  const revealBound = useCallback(
    (node: HTMLDivElement | null) => {
      if (node === null || boundAssetId === undefined || revealed.current === boundAssetId) return;
      revealed.current = boundAssetId;
      node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    },
    [boundAssetId],
  );

  /** Wraps a tile's control in a tooltip where the caller asked for one, and leaves it alone
   *  otherwise - the galleries this grid was written for keep the browser's own. */
  const named = (control: ReactElement, text: string) =>
    namesOnHover ? (
      <Tooltip>
        <TooltipTrigger asChild>{control}</TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    ) : (
      control
    );

  const grid = (
    <div data-testid="media-grid" className={`grid gap-2 ${COLUMN_CLASS[columns]}`}>
      {tiles.map((tile) => {
        const selected = picking && selectedIds.has(tile.id);
        // What the frame marks, which each mode answers its own way: the canvas frame's asset on a
        // binding surface, the open one in the library.
        const outlined = picking ? tile.id === openMediaId : tile.assetId === boundAssetId;
        // Open beats ticked where a tile is both, as it does in the table: the checkbox still says
        // it is in the selection, where without the fill nothing would say the panel is on it.
        const frame = outlined
          ? picking
            ? FRAME_CLASS.open
            : FRAME_CLASS.bound
          : selected
            ? FRAME_CLASS.selected
            : FRAME_CLASS.default;
        const isVideo = tile.mediaType === 'video';
        const playing = isVideo && hovered === tile.id && tile.videoUrl !== undefined;

        const label = isVideo ? t('brandMedia.videoTile', { title: tile.title }) : tile.title;
        const usageKey = adUsageLabelKey(tile.tags);
        // A reference tile has no control to name, so the picture carries the name instead; a
        // pickable one is named by its button and the image stays decorative.
        const interactive = onChoose !== undefined;
        // Clicking the marked tile empties the slot rather than re-choosing what it already holds.
        // Never in the library's picking mode, where the mark means "this is the one open" and
        // clicking it again should leave it open.
        const clears = onClear !== undefined && !picking && outlined;
        // The words the control is named by, so the tooltip and the accessible name cannot
        // drift: a floating label that disagreed with the one a screen reader reads would be
        // two different answers to the same question.
        const name = clears ? t('brandMedia.clearBoundTile', { title: tile.title }) : label;
        const shared = {
          'data-testid': 'media-tile',
          ...(revealsBound && !picking && outlined ? { ref: revealBound } : {}),
          // Dropped where the tooltip below says the same thing, or a hover draws both.
          ...(namesOnHover ? {} : { title: tile.description ?? tile.title }),
          // The state is carried by the frame, never over the photograph. A mark drawn against
          // whatever the picture happens to be is unreadable over half a library, which is what a
          // ring stacked on a border was compensating for; the tile holds the picture inside a
          // field of its own instead, so a fill reads the same against any of them. Every tile is
          // framed, not only the chosen one -- padding the picture at the moment it is picked would
          // shrink it and jog the grid.
          // `isolate` keeps the checkbox's z-index inside the tile: the library's toolbar is sticky
          // at z-10 and the grid comes after it, so an unscoped layer here would sit over it.
          className: `group relative isolate aspect-square rounded-md border p-1 transition-colors duration-fast ease-out ${frame}`,
          onMouseEnter: () => {
            setPlayingId(null);
            setHovered(tile.id);
          },
          onMouseLeave: () => setHovered((current) => (current === tile.id ? null : current)),
        };

        const failed = tile.url === undefined || imageFailed.has(tile.id);

        const contents = (
          <>
            {/* No element until the url resolves. An empty src is not an absent image: the browser
                treats it as a request for the current page and downloads it again. */}
            {tile.url !== undefined && !imageFailed.has(tile.id) && (
              <img
                src={tile.url}
                alt={interactive ? '' : label}
                // A grid can hold every row the listing returned, which is far more than fits any
                // surface it renders on. The urls are all resolved up front -- an img with no src
                // cannot defer anything -- so this is what keeps the bytes to what someone looks at.
                loading="lazy"
                className="size-full object-cover"
                onError={() => setImageFailed((current) => new Set(current).add(tile.id))}
              />
            )}

            {/* Reports the failure instead of leaving the tile blank -- a silent blank gives a
                person nothing to act on, whether the url never resolved or the image it pointed
                at could not be fetched. */}
            {failed && (
              <span
                data-testid="media-tile-failed"
                title={t('brandMedia.tileImageFailed')}
                className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground"
              >
                <ImageOffIcon className="size-5" aria-hidden="true" />
                <span className="sr-only">{t('brandMedia.tileImageFailed')}</span>
              </span>
            )}

            {/* Ingestion's reading of the shot, not a decision anyone made, so it sits on the
                picture as a label rather than in the tile's accessible name. */}
            {showAdUsage && usageKey !== undefined && (
              <span
                data-testid="media-tile-ad-usage"
                aria-hidden="true"
                className="absolute left-1 top-1 rounded bg-background/85 px-1 py-0.5 text-label-xxs font-medium uppercase leading-none tracking-wide text-foreground"
              >
                {t(usageKey)}
              </span>
            )}

            {/* Over the poster rather than instead of it, so the tile never goes blank while the
                first frame decodes, and only shown once it plays: a clip with transparency drawn
                over its own poster shows both at once. On the checkerboard, so it covers the
                poster wherever it is transparent. Muted is what lets it autoplay at all. */}
            {playing && (
              <video
                data-testid="media-tile-video"
                src={tile.videoUrl}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden="true"
                onPlaying={() => setPlayingId(tile.id)}
                className={`absolute inset-0 size-full bg-transparency-checker object-cover transition-opacity duration-fast ease-out ${
                  playingId === tile.id ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <track kind="captions" />
              </video>
            )}

            {/* Stays up while playing: a tile that dropped its badge on hover would read as a still
                that happens to be moving. The button's own label already says video, so this is
                decorative. */}
            {isVideo && (
              <span
                data-testid="media-tile-video-badge"
                aria-hidden="true"
                className="absolute bottom-1 left-1 flex size-4 items-center justify-center rounded-full bg-black/60 text-white"
              >
                <PlayIcon className="size-2.5" />
              </span>
            )}

            {/* Only while it is still expected: a row that aged out says so in the detail panel,
                where there is room to offer the retry. A spinner that never stopped would be a
                promise the grid cannot keep. */}
            {tile.ingestion === 'pending' && (
              <span
                data-testid="media-tile-ingesting"
                title={t('brandMedia.ingestingTile')}
                aria-hidden="true"
                className="absolute bottom-1 right-1 flex size-4 items-center justify-center rounded-full bg-black/60 text-white"
              >
                <Loader2Icon className="size-2.5 animate-spin" />
              </span>
            )}

            {/* The canvas's marker: this is the asset the frame currently uses. Top-right, which is
                free here because a binding surface draws no checkbox.

                Drawn in the page's own colours rather than the frame's, so it inverts against the
                fill behind it -- the same trick the checkbox plays with its opaque background.
                This is the one mark that lands on the photograph instead of the frame, and a disc
                the colour of the fill is a smudge over any shot that happens to match it. */}
            {!picking && outlined && (
              <span
                data-testid="media-tile-bound"
                className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-background text-foreground"
              >
                <CheckIcon className="size-3" />
              </span>
            )}
          </>
        );

        if (!interactive) {
          return (
            <div key={tile.id} {...shared}>
              <div className={PICTURE_CLASS}>{contents}</div>
            </div>
          );
        }

        return (
          <div key={tile.id} {...shared}>
            <div className={PICTURE_CLASS}>
              {contents}

              {/* Over the whole tile rather than around it. A card that contains a checkbox cannot be
                a button -- nesting one interactive element in another is invalid, and a screen
                reader is given no way to reach the inner one -- so the picture keeps its click
                through an overlay and the checkbox sits above it as a sibling. */}
              {/* Two questions, two attributes. On a binding surface the tile is a toggle -- this
                  frame either uses this asset or it does not -- so it is pressed. In the library it
                  is one of a set of things to look at, and the one being looked at is the current
                  one. */}
              {/* Named for what the click will do, not for what the tile holds: on the marked tile
                  of a toggling grid that is removing the picture, and a label still reading as the
                  photograph's name would promise the opposite of what happens. */}
              {named(
                <button
                  type="button"
                  aria-label={name}
                  {...(picking
                    ? { 'aria-current': outlined ? ('true' as const) : undefined }
                    : { 'aria-pressed': outlined })}
                  className="absolute inset-0"
                  onClick={() => (clears ? onClear() : onChoose(tile.assetId))}
                />,
                name,
              )}

              {/* Shown while the tile is pointed at or holds focus, which is what says it can be
                  ticked; a ticked one stays visible, because a selection you can only see by
                  hovering is a selection you will lose track of. Mounted either way and revealed by
                  opacity, so it fades in rather than arriving, and `focus-within` rather than
                  `focus-visible` because the focus now lands on a child. */}
              {onToggleSelect !== undefined && (
                <span
                  data-testid="media-tile-check"
                  data-checked={selected}
                  className={`absolute right-1 top-1 z-10 transition-opacity duration-fast ease-out ${
                    selected
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                  }`}
                >
                  <Checkbox
                    aria-label={t('brandMedia.selectAsset', { title: tile.title })}
                    checked={selected}
                    // Opaque while unchecked: the default is transparent, which over a photograph is
                    // a hairline square that reads as an artefact rather than as a control.
                    className="bg-background/80"
                    onCheckedChange={() => onToggleSelect(tile.id)}
                  />
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Its own provider rather than one borrowed from a host: Radix throws without one above it,
  // and this grid has eight consumers that know nothing about each other.
  return namesOnHover ? <TooltipProvider>{grid}</TooltipProvider> : grid;
};
